import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PenLine, Wallet, ShoppingBag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import type { WizardData } from "@/pages/CreateCard";

interface Props {
  open: boolean;
  updateData: (u: Partial<WizardData>) => void;
}

// Shown over the Create Card page before the wizard renders so the
// user's participation choice is captured up-front without eating a
// wizard step. Renders as a bottom sheet on mobile (easier thumb
// reach) and as a centered dialog on desktop. Not dismissable by
// ESC or outside-click; the user either picks an option or backs
// out via the app header.
const PurposeDialog = ({ open, updateData }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const selectSignOnly = () => {
    updateData({
      purposeSelected: true,
      collectionEnabled: false,
      type: "card_only",
    });
  };

  const selectSignAndChipIn = () => {
    updateData({
      purposeSelected: true,
      collectionEnabled: true,
      type: "card_collection",
    });
  };

  const selectPoolForProduct = () => {
    navigate("/create/product");
  };

  const title = t("wizard.purpose.title", { defaultValue: "What would you like to do?" });
  const subtitle = t("wizard.purpose.subtitle", {
    defaultValue: "Pick one. You can change this later.",
  });

  const options = (
    <div className="grid gap-3 mt-2">
      <button
        type="button"
        onClick={selectSignOnly}
        className="w-full text-start rounded-xl border border-border p-5 transition-all hover:border-foreground/30"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <PenLine size={18} className="text-foreground" />
          </div>
          <div className="flex-1">
            <div className="text-base font-semibold text-foreground">
              {t("wizard.purpose.signOnly", { defaultValue: "Collect signatures & wishes" })}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {t("wizard.purpose.signOnlyDesc", {
                defaultValue: "Gather messages, photos and signatures from the group.",
              })}
            </div>
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={selectSignAndChipIn}
        className="w-full text-start rounded-xl border border-border p-5 transition-all hover:border-foreground/30"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <Wallet size={18} className="text-foreground" />
          </div>
          <div className="flex-1">
            <div className="text-base font-semibold text-foreground">
              {t("wizard.purpose.signAndChipIn", { defaultValue: "Chip in money for a goal" })}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {t("wizard.purpose.signAndChipInDesc", {
                defaultValue: "Pool money together to buy a specific gift or item.",
              })}
            </div>
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={selectPoolForProduct}
        className="relative w-full text-start rounded-xl border border-border p-5 transition-all hover:border-foreground/30"
      >
        <span className="absolute top-2 end-2 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
          {t("wizard.purpose.newBadge", { defaultValue: "New" })}
        </span>
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <ShoppingBag size={18} className="text-foreground" />
          </div>
          <div className="flex-1 pe-10">
            <div className="text-base font-semibold text-foreground">
              {t("wizard.purpose.poolForProduct", { defaultValue: "Collect money only" })}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {t("wizard.purpose.poolForProductDesc", {
                defaultValue: "Pool cash and let the recipient spend it however they like.",
              })}
            </div>
          </div>
        </div>
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl [&>button]:hidden max-h-[90vh] overflow-y-auto"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle className="text-2xl font-bold font-serif">{title}</SheetTitle>
            <SheetDescription>{subtitle}</SheetDescription>
          </SheetHeader>
          {options}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-lg [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold font-serif">{title}</DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>
        {options}
      </DialogContent>
    </Dialog>
  );
};

export default PurposeDialog;
